type ArticlePersonFields = {
  authorName?: string
  slug?: string
  [key: string]: unknown
}

export type ArticlePersonEntry = {
  fields: ArticlePersonFields
  sys?: { id?: string }
}

type ArticleRole = 'author' | 'coauthor' | 'reviewer'

type ArticleRoleReference = {
  sys?: { id?: string }
  fields?: { name?: string; label?: string }
}

export type ArticleFields = {
  author?: unknown
  reviewedBy?: unknown
  authors?: {
    v?: number
    mode?: string
    pairs?: Array<{
      reference?: ArticleRoleReference
      value?: unknown
    }>
  }
}

type ContentfulEntryLink = { sys?: { id?: string; linkType?: string; type?: string } }

const toPersonEntry = (value: unknown): ArticlePersonEntry | null => {
  if (!value || typeof value !== 'object') return null

  const entry = value as { fields?: unknown; sys?: { id?: string } }
  const fields = entry.fields && typeof entry.fields === 'object' ? entry.fields : value
  if (
    !fields ||
    typeof fields !== 'object' ||
    typeof (fields as ArticlePersonFields).authorName !== 'string'
  ) {
    return null
  }

  return {
    fields: fields as ArticlePersonFields,
    ...(entry.sys ? { sys: entry.sys } : {}),
  }
}

const toPersonEntries = (value: unknown): ArticlePersonEntry[] => {
  const values = Array.isArray(value) ? value : value ? [value] : []
  return values.map(toPersonEntry).filter((entry): entry is ArticlePersonEntry => entry !== null)
}

const getRole = (reference?: ArticleRoleReference): ArticleRole | null => {
  const roleValues = [reference?.sys?.id, reference?.fields?.name, reference?.fields?.label]
    .filter((value): value is string => Boolean(value))
    .map((value) => value.toLowerCase().replace(/[^a-z]/g, ''))

  if (roleValues.some((value) => value.includes('coauthor'))) return 'coauthor'
  if (roleValues.some((value) => value.includes('reviewer') || value.includes('reviewedby'))) {
    return 'reviewer'
  }
  if (roleValues.some((value) => value.includes('author') || value.includes('writtenby'))) {
    return 'author'
  }

  return null
}

export function getArticlePeople(fields: ArticleFields = {}) {
  const pairs = fields.authors?.mode === 'pairs' ? fields.authors.pairs ?? [] : []
  const getEntriesForRole = (role: ArticleRole) =>
    pairs
      .filter((pair) => getRole(pair.reference) === role)
      .flatMap((pair) => toPersonEntries(pair.value))

  const authorPairs = pairs.filter((pair) => getRole(pair.reference) === 'author')
  const reviewerPairs = pairs.filter((pair) => getRole(pair.reference) === 'reviewer')

  return {
    authors: authorPairs.length ? getEntriesForRole('author') : toPersonEntries(fields.author),
    coauthors: getEntriesForRole('coauthor'),
    reviewers: reviewerPairs.length
      ? getEntriesForRole('reviewer')
      : toPersonEntries(fields.reviewedBy),
  }
}

export function getArticlePeopleEntryIds(fields: ArticleFields = {}) {
  const pairs = fields.authors?.mode === 'pairs' ? fields.authors.pairs ?? [] : []

  return [
    ...new Set(
      pairs.flatMap((pair) => {
        const values = Array.isArray(pair.value) ? pair.value : [pair.value]
        return values
          .map((value) =>
            value && typeof value === 'object' ? (value as ContentfulEntryLink) : null
          )
          .filter((value) => value?.sys?.type === 'Link' && value.sys.linkType === 'Entry')
          .map((value) => value?.sys?.id)
          .filter((id): id is string => Boolean(id))
      })
    ),
  ]
}

export function resolveArticlePeopleLinks(fields: ArticleFields, entries: ArticlePersonEntry[]) {
  const entriesById = new Map(
    entries.filter((entry) => entry.sys?.id).map((entry) => [entry.sys!.id!, entry])
  )
  const pairs = fields.authors?.pairs ?? []

  return {
    ...fields,
    authors: fields.authors
      ? {
          ...fields.authors,
          pairs: pairs.map((pair) => {
            const resolveValue = (value: unknown) => {
              if (!value || typeof value !== 'object') return value
              const link = value as ContentfulEntryLink
              const id =
                link.sys?.type === 'Link' && link.sys.linkType === 'Entry' ? link.sys.id : null
              return id ? entriesById.get(id) ?? value : value
            }

            return {
              ...pair,
              value: Array.isArray(pair.value)
                ? pair.value.map(resolveValue)
                : resolveValue(pair.value),
            }
          }),
        }
      : undefined,
  }
}
